import React, { useEffect, useMemo, useState } from "react";

import { Link, useNavigate, useParams } from "react-router-dom";

import {
  Box,
  Paper,
  Checkbox,
  IconButton,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  InputAdornment,
  Container,
  Breadcrumbs,
  useTheme,
  Menu,
  Snackbar,
  Alert,
  ListItemIcon,
  ListItemText,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  TablePagination,
  Avatar,
  Divider,
  Chip,
  CircularProgress,
  Card,
  CardContent,
} from "@mui/material";

import { DataGrid, useGridApiRef } from "@mui/x-data-grid";

import MoreVertIcon from "@mui/icons-material/MoreVert";

import SearchIcon from "@mui/icons-material/Search";

import UploadFileIcon from "@mui/icons-material/UploadFile";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";

import NavigateNextIcon from "@mui/icons-material/NavigateNext";

import {
  Person,
  Phone,
  Email,
  Business,
  Save,
  Cancel,
  Close,
  Refresh,
  Mail as MailIcon,
  EditNotifications,
  DatasetLinked as DatasetLinkedIcon,
  PersonAdd,
  AttachEmail,
  Edit as EditIcon,
  Download,
  ViewList,
  GridView,
  People,
  CheckCircle,
  PendingActions,
  ErrorOutline,
} from "@mui/icons-material";

import { useSelector } from "react-redux";

import { ShadowBox } from "../../../../components/styles/styledComponent";

import ClickableChip from "../../../../components/common/ClickableChip";

import AddEmployee from "./AddEmployee";

import SuccessModal from "../../../../components/common/modal/SuccessModal";

import { useErrorModal } from "../../../../hooks/useErrorModal";

import {
  getEmployees,
  getEmployeesByEntity,
  regenerateDocumentUploadToken,
  exportEmployeesToExcel,
} from "../../../../api/employeeApi";

import { getStatusColor } from "../../../../utils/helper/tablehelper";

import CustomNoResultsOverlay from "./components/CustomNoResultsOverlay";

import { IconHoverButton } from "../../../../components/common/button";

import { adminMenuItems } from "../../../../config/layout";

import PermissionGuard from "../../../../components/PermissionGuard";

import AlertDialog from "../../../../components/common/AlertDialog";

import useAlertDialog from "../../../../hooks/useAlertDialog";
import { useGetRolesQuery } from "../../../../services/roleApi";
import { getEntities } from "../../../../api/entityApi";

// Premium Stats Card Subcomponent
const StatCard = ({ title, count, icon, color, gradient }) => {
  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: "16px",
        background: "rgba(255, 255, 255, 0.9)",
        backdropFilter: "blur(8px)",
        border: "1px solid rgba(226, 232, 240, 0.8)",
        position: "relative",
        overflow: "hidden",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.02)",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 30px rgba(117, 34, 209, 0.08)",
          borderColor: color,
          "& .stat-icon-wrapper": {
            transform: "scale(1.1)",
            backgroundColor: color,
            color: "#fff",
          }
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "-50%",
          right: "-30%",
          width: "120px",
          height: "120px",
          borderRadius: "50%",
          background: gradient,
          opacity: 0.15,
          filter: "blur(20px)",
          pointerEvents: "none",
        }}
      />
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              fontSize: "0.725rem",
              mb: 0.5,
            }}
          >
            {title}
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 800, color: "text.primary", letterSpacing: "-0.02em" }}>
            {count}
          </Typography>
        </Box>
        <Box
          className="stat-icon-wrapper"
          sx={{
            width: 48,
            height: 48,
            borderRadius: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: `${color}15`,
            color: color,
            transition: "all 0.3s ease",
          }}
        >
          {icon}
        </Box>
      </Box>
    </Box>
  );
};

// Premium Employee Card Subcomponent (Grid View)
const EmployeeCard = ({ employee, onActionOpen, navigate }) => {
  const statusColor = employee.status === "COMPLETED" ? "#10B981" : employee.status === "PENDING" ? "#F59E0B" : "#EF4444";
  
  const getStatusChipStyle = (status) => {
    switch (status) {
      case "COMPLETED":
        return { bg: "rgba(16, 185, 129, 0.1)", color: "#059669" };
      case "PENDING":
      case "IN_PROGRESS":
        return { bg: "rgba(245, 158, 11, 0.1)", color: "#D97706" };
      case "FAILED":
      case "REJECTED":
      default:
        return { bg: "rgba(239, 68, 68, 0.1)", color: "#DC2626" };
    }
  };

  const getEmpTypeLabel = (type) => {
    return type ? type.replace("_", " ").toLowerCase() : "full time";
  };

  const statusStyle = getStatusChipStyle(employee.status);

  const stringToColor = (string) => {
    let hash = 0;
    for (let i = 0; i < string.length; i += 1) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = "#";
    for (let i = 0; i < 3; i += 1) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }
    return color;
  };

  const avatarColor = stringToColor(employee.name);

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: "16px",
        border: "1px solid rgba(226, 232, 240, 0.8)",
        backgroundColor: "#fff",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 30px rgba(117, 34, 209, 0.08)",
          borderColor: "rgba(117, 34, 209, 0.3)",
        },
      }}
    >
      <Box sx={{ height: 4, width: "100%", backgroundColor: statusColor }} />

      <CardContent sx={{ p: 3, flexGrow: 1, display: "flex", flexDirection: "column" }}>
        <IconButton
          size="small"
          onClick={(e) => onActionOpen(e, employee)}
          sx={{ position: "absolute", top: 12, right: 12, color: "text.secondary" }}
        >
          <MoreVertIcon sx={{ fontSize: 20 }} />
        </IconButton>

        <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              bgcolor: avatarColor,
              color: "#fff",
              fontWeight: 700,
              fontSize: "1rem",
              boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
            }}
          >
            {employee.name
              ? employee.name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .substring(0, 2)
                  .toUpperCase()
              : "EE"}
          </Avatar>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              noWrap
              onClick={() => navigate(`/my-team/employee-directory/${employee.id}`)}
              sx={{
                fontWeight: 700,
                color: "#030007",
                cursor: "pointer",
                "&:hover": { color: "#7522D1", textDecoration: "underline" },
              }}
            >
              {employee.name}
            </Typography>
            <Typography variant="caption" sx={{ color: "#7522D1", fontWeight: 600, display: "block" }}>
              #{employee.empNo || "N/A"}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 1.5, borderColor: "rgba(226, 232, 240, 0.8)" }} />

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2, flexGrow: 1, mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
            <Business sx={{ fontSize: 16, color: "text.secondary" }} />
            <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }} noWrap>
              {employee.department || "N/A"}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
            <Person sx={{ fontSize: 16, color: "text.secondary" }} />
            <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }} noWrap>
              {employee.role || "N/A"}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
            <Email sx={{ fontSize: 16, color: "text.secondary" }} />
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", fontSize: "0.825rem" }}
              noWrap
              title={employee.email}
            >
              {employee.email || "N/A"}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 1.5, borderColor: "rgba(226, 232, 240, 0.8)", mt: "auto" }} />

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Chip
            label={getEmpTypeLabel(employee.employeeType)}
            size="small"
            sx={{
              textTransform: "capitalize",
              fontWeight: 600,
              fontSize: "0.75rem",
              backgroundColor: "rgba(117, 34, 209, 0.05)",
              color: "#7522D1",
              border: "1px solid rgba(117, 34, 209, 0.1)",
            }}
          />
          <Chip
            label={employee.status}
            size="small"
            sx={{
              fontWeight: 700,
              fontSize: "0.725rem",
              backgroundColor: statusStyle.bg,
              color: statusStyle.color,
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

// Premium transition styles for filters
const filterInputStyle = {
  borderRadius: "10px",
  backgroundColor: "#fff",
  transition: "all 0.2s ease-in-out",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(226, 232, 240, 0.8)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(117, 34, 209, 0.4)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#7522D1",
  },
};

const EmployeeDirectory = () => {
  const navigate = useNavigate();

  const { tab } = useParams();

  const { showError, ErrorModal } = useErrorModal();

  const { alertDialog, showAlert, closeAlert } = useAlertDialog();

  const [activeTab, setActiveTab] = useState(0);

  const [searchQuery, setSearchQuery] = useState("");

  const [employeeTypeFilter, setEmployeeTypeFilter] = useState("ALL");

  const [statusFilter, setStatusFilter] = useState("ALL");

  const [openModal, setOpenModal] = useState(false);

  const [openBulkUploadModal, setOpenBulkUploadModal] = useState(false);

  const [openAddEmployeeModal, setOpenAddEmployeeModal] = useState(false);

  const [employees, setEmployees] = useState([]);

  const [loading, setLoading] = useState(true);

  const [bulkUploadFile, setBulkUploadFile] = useState(null);

  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const [entities, setEntities] = useState([]);
  const [locations, setLocations] = useState([]);
  const [entityFilter, setEntityFilter] = useState("ALL");
  const [locationFilter, setLocationFilter] = useState("ALL");

  const { data: roles = [] } = useGetRolesQuery({ status: "Active" });

  // States for Action Menu

  const [anchorEl, setAnchorEl] = useState(null);

  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const openMenu = Boolean(anchorEl);

  // States for Notifications

  const [snackbar, setSnackbar] = useState({
    open: false,

    message: "",

    severity: "success",
  });

  const handleDownloadExcel = async () => {
    try {
      setLoading(true);
      const filters = {
        entityId: entityFilter === "ALL" ? undefined : entityFilter,
        status: statusFilter === "ALL" ? undefined : statusFilter,
        employmentType: employeeTypeFilter === "ALL" ? undefined : employeeTypeFilter,
        jobLocation: locationFilter === "ALL" ? undefined : locationFilter,
        search: searchQuery || undefined,
      };

      const blob = await exportEmployeesToExcel(filters);

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Employees.xlsx');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);

      setSnackbar({
        open: true,
        message: "Excel file downloaded successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to download Excel:", error);
      setSnackbar({
        open: true,
        message: "Failed to download Excel file",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Define status options for EmployeeDirectory (more limited set)

  const statusOptions = [
    { value: "PENDING", label: "Pending" },

    { value: "COMPLETED", label: "Completed" },

    { value: "FAILED", label: "Failed" },
  ];

  const handleMenuOpen = (event, employee) => {
    setAnchorEl(event.currentTarget);

    setSelectedEmployee(employee);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);

    setSelectedEmployee(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleStatusChange = (employeeId, newStatus) => {
    // Update the state to reflect the change

    setEmployees((prevEmployees) =>
      prevEmployees.map((emp) =>
        emp.id === employeeId ? { ...emp, status: newStatus } : emp,
      ),
    );

    // Add your API call here when ready

    // await updateEmployeeStatus(employeeId, newStatus);
  };

  const handleResendLink = async () => {
    if (!selectedEmployee) return;

    try {
      handleMenuClose();

      setLoading(true);

      const result = await regenerateDocumentUploadToken(selectedEmployee.id);

      if (result.success) {
        setSnackbar({
          open: true,

          message:
            "Onboarding link regenerated and sent successfully to " +
            selectedEmployee.email,

          severity: "success",
        });
      } else {
        setSnackbar({
          open: true,

          message: result.message || "Failed to resend link",

          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error resending link:", error);

      setSnackbar({
        open: true,

        message: "An error occurred while resending the link",

        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = () => {
    if (selectedEmployee) {
      navigate(`/my-team/employee-directory/${selectedEmployee.id}`);

      handleMenuClose();
    }
  };

  const handleEdit = () => {
    if (selectedEmployee) {
      navigate(`/my-team/add-employee/${selectedEmployee.id}/basic-details`);

      handleMenuClose();
    }
  };

  // HR Onboarding Form State

  const theme = useTheme();

  const [onboardingFormData, setOnboardingFormData] = useState({
    firstName: "",

    middleName: "",

    lastName: "",

    mobileNumber: "",

    email: "",

    department: "",

    role: "",

    entity: "",
  });

  const [onboardingFormErrors, setOnboardingFormErrors] = useState({});

  // const apiRef = useGridApiRef();

  // Get tabs from layout configuration

  const myTeamItem = adminMenuItems.find((item) => item.id === "my-team");

  const tabs = useMemo(() => myTeamItem?.tabs || [], []);

  useEffect(() => {
    if (!tab) {
      setActiveTab(0);
      return;
    }

    const nextIndex = tabs.findIndex((t) => t.value === tab);
    if (nextIndex !== -1) {
      setActiveTab(nextIndex);
    }
  }, [tab, tabs]);

  // Fetch Entities (Backend filters by user scope)
  useEffect(() => {
    const fetchEntitiesList = async () => {
      const res = await getEntities();
      if (res.success) setEntities(res.data);
    };
    if (isAuthenticated) fetchEntitiesList();
  }, [user, isAuthenticated]);

  // Fetch Locations when entity is selected
  useEffect(() => {
    const fetchLocationsList = async () => {
      if (entityFilter !== "ALL") {
        const { getLocations } = await import("../../../../api/locationApi");
        const res = await getLocations({ entityId: entityFilter });
        if (res.success) setLocations(res.data);
        setLocationFilter("ALL"); // Reset location when entity changes
      } else {
        setLocations([]);
        setLocationFilter("ALL");
      }
    };
    fetchLocationsList();
  }, [entityFilter]);

  useEffect(() => {
    const fetchEmployeesData = async () => {
      try {
        setLoading(true);

        const hierarchyLevel = user?.role?.hierarchyLevel;
        let filters = {};

        if (hierarchyLevel === "Level 1") {
          if (entityFilter !== "ALL") filters.entityId = entityFilter;
          if (locationFilter !== "ALL") filters.workLocation = locationFilter;
        } else {
          const entityId = user?.entityId || user?.entity_id;
          filters = entityId ? { entityId } : {};
        }

        const response = await getEmployees(filters);

        if (response && response.success) {
          const rows = response.data.rows || response.data || [];

          const formattedData = rows.map((emp) => ({
            ...emp,

            id: emp.id || emp.employeeId,

            empNo: emp.employeeId || emp.empNo,

            name:
              `${emp.firstName || ""} ${emp.lastName || ""}`.trim() || "N/A",

            designation: emp.designation?.name || "N/A",

            role:
              emp.role?.roleName ||
              emp.role?.name ||
              emp.roleName ||
              emp.role ||
              "N/A",

            department:
              emp.department?.deptName ||
              emp.department?.name ||
              emp.departmentName ||
              emp.department ||
              "N/A",

            status: (emp.status || "PENDING").toUpperCase(),

            employeeType: (
              emp.employmentType ||
              emp.employeeType ||
              "FULL_TIME"
            )
              .toUpperCase()
              .replace("-", "_"),
          }));

          setEmployees(formattedData);
        }
      } catch (error) {
        console.error("Critical error in fetchEmployeesData:", error);

        showError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeesData();
  }, [user, isAuthenticated, entityFilter, locationFilter]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);

    navigate(`/my-team/pre-onboarding/${tabs[newValue].value}`);
  };

  // Get breadcrumbs - use active tab's breadcrumbs if available, otherwise use default

  const currentBreadcrumbs = useMemo(() => {
    const currentnavItem = myTeamItem?.subItems?.find(
      (item) => item.id === "employee-directory",
    );

    if (currentnavItem && currentnavItem?.tabs) {
      if (activeTab >= 0 && tabs[activeTab]?.breadcrumbs) {
        return tabs[activeTab].breadcrumbs;
      }
    }

    return currentnavItem?.breadcrumbs;
  }, [activeTab, tabs]);

  const columns = [
    {
      field: "empNo",
      headerName: "EMPLOYEE NO.",
      width: 150,
      resizable: true,
      headerAlign: "center",
      align: "left",
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            variant="body2"
            component="span"
            sx={{
              color: "#7522D1",
              fontWeight: 700,
              cursor: "pointer",
              "&:hover": {
                color: "#5e1ba8",
                textDecoration: "underline",
              },
            }}
            onClick={() =>
              navigate(`/my-team/employee-directory/${params.row.id}`)
            }
          >
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "name",
      headerName: "NAME",
      width: 220,
      resizable: true,
      headerAlign: "center",
      align: "left",
      renderCell: (params) => {
        const name = params.value || "N/A";
        const stringToColor = (str) => {
          let hash = 0;
          for (let i = 0; i < str.length; i += 1) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
          }
          let col = "#";
          for (let i = 0; i < 3; i += 1) {
            const val = (hash >> (i * 8)) & 0xff;
            col += `00${val.toString(16)}`.slice(-2);
          }
          return col;
        };
        const avatarColor = stringToColor(name);
        const initials = name
          ? name
              .split(" ")
              .map((n) => n[0])
              .join("")
              .substring(0, 2)
              .toUpperCase()
          : "EE";
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, height: "100%" }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: avatarColor,
                color: "#fff",
                fontWeight: 700,
                fontSize: "0.8rem",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              }}
            >
              {initials}
            </Avatar>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: "#030007",
                cursor: "pointer",
                "&:hover": {
                  color: "#7522D1",
                },
              }}
              onClick={() => navigate(`/my-team/employee-directory/${params.row.id}`)}
            >
              {name}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "designation",
      headerName: "DESIGNATION",
      width: 180,
      resizable: true,
      headerAlign: "center",
      align: "left",
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography variant="body2" sx={{ fontWeight: 500, color: "text.secondary" }}>
            {params.value || "N/A"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "email",
      headerName: "EMAIL",
      width: 220,
      resizable: true,
      headerAlign: "center",
      align: "left",
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", fontSize: "0.875rem" }}
            title={params.value}
          >
            {params.value || "N/A"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "role",
      headerName: "SYSTEM ROLE",
      width: 160,
      resizable: true,
      headerAlign: "center",
      align: "left",
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography variant="body2" sx={{ fontWeight: 500, color: "text.secondary" }}>
            {params.value || "N/A"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "department",
      headerName: "DEPARTMENT",
      width: 160,
      resizable: true,
      headerAlign: "center",
      align: "left",
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography variant="body2" sx={{ fontWeight: 500, color: "text.secondary" }}>
            {params.value || "N/A"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "employeeType",
      headerName: "EMPLOYEE TYPE",
      width: 150,
      resizable: true,
      headerAlign: "center",
      align: "left",
      renderCell: (params) => {
        const label = params.value ? params.value.replace("_", " ").toLowerCase() : "full time";
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Chip
              label={label}
              size="small"
              sx={{
                textTransform: "capitalize",
                fontWeight: 600,
                fontSize: "0.75rem",
                backgroundColor: "rgba(117, 34, 209, 0.05)",
                color: "#7522D1",
                border: "1px solid rgba(117, 34, 209, 0.1)",
              }}
            />
          </Box>
        );
      },
    },
    {
      field: "status",
      headerName: "STATUS",
      width: 150,
      resizable: true,
      headerAlign: "center",
      align: "left",
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <ClickableChip
            status={params.value}
            statusOptions={statusOptions}
            allowUpdate={true} // Enable updates to see dropdown
            onStatusChange={(newStatus) =>
              handleStatusChange(params.row.id, newStatus)
            }
          />
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 80,
      resizable: false,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
          <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.row)}>
            <MoreVertIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  // Filter data based on search and filters

  const filteredData = useMemo(() => {
    return employees.filter((employee) => {
      const name = employee.name || "";

      const empNo = employee.empNo || employee.employeeId || "";

      const email = employee.email || "";

      const matchesSearch =
        searchQuery === "" ||
        name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        empNo.toLowerCase().includes(searchQuery.toLowerCase()) ||
        email.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesEmployeeType =
        employeeTypeFilter === "ALL" ||
        employee.employeeType === employeeTypeFilter;

      const matchesStatus =
        statusFilter === "ALL" || employee.status === statusFilter;

      return matchesSearch && matchesEmployeeType && matchesStatus;
    });
  }, [employees, searchQuery, employeeTypeFilter, statusFilter]);

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleOpenBulkUploadModal = () => {
    setOpenBulkUploadModal(true);
  };

  const handleCloseBulkUploadModal = () => {
    setOpenBulkUploadModal(false);

    setBulkUploadFile(null);
  };

  const handleOpenAddEmployeeModal = () => {
    setOpenAddEmployeeModal(true);
  };

  const handleCloseAddEmployeeModal = () => {
    setOpenAddEmployeeModal(false);
  };

  const handleBulkUploadFileChange = (e) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;

    setBulkUploadFile(file);
  };

  const handleBulkUploadSubmit = () => {
    if (!bulkUploadFile) return;

    setOpenBulkUploadModal(false);
  };

  // HR Onboarding Form Data and Handlers

  const departments = [
    "Engineering",

    "Human Resources",

    "Finance",

    "Marketing",

    "Sales",

    "Operations",

    "IT",

    "Administration",
  ];

  const handleOnboardingInputChange = (field) => (event) => {
    setOnboardingFormData({
      ...onboardingFormData,

      [field]: event.target.value,
    });

    // Clear error for this field when user starts typing

    if (onboardingFormErrors[field]) {
      setOnboardingFormErrors({
        ...onboardingFormErrors,

        [field]: "",
      });
    }
  };

  const validateOnboardingForm = () => {
    const newErrors = {};

    if (!onboardingFormData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!onboardingFormData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!onboardingFormData.mobileNumber.trim()) {
      newErrors.mobileNumber = "Mobile number is required";
    } else if (
      !/^[0-9]{10}$/.test(onboardingFormData.mobileNumber.replace(/\s/g, ""))
    ) {
      newErrors.mobileNumber = "Please enter a valid 10-digit mobile number";
    }

    if (!onboardingFormData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(onboardingFormData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!onboardingFormData.department) {
      newErrors.department = "Department is required";
    }

    if (!onboardingFormData.role) {
      newErrors.role = "Role is required";
    }

    if (!onboardingFormData.entity) {
      newErrors.entity = "Entity is required";
    }

    setOnboardingFormErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleOnboardingSubmit = (event) => {
    event.preventDefault();

    if (validateOnboardingForm()) {
      showAlert({
        title: "Success",

        message: "Employee onboarded successfully!",

        severity: "success",

        onConfirm: () => {
          // Reset form and close modal

          setOnboardingFormData({
            firstName: "",

            middleName: "",

            lastName: "",

            mobileNumber: "",

            email: "",

            department: "",

            role: "",

            entity: "",
          });

          setOnboardingFormErrors({});

          setOpenModal(false);
        },
      });
    }
  };

  const handleOnboardingReset = () => {
    setOnboardingFormData({
      firstName: "",

      middleName: "",

      lastName: "",

      mobileNumber: "",

      email: "",

      department: "",

      role: "",

      entity: "",
    });

    setOnboardingFormErrors({});
  };

  // Redesign: Stats calculation
  const stats = useMemo(() => {
    const total = employees.length;
    const completed = employees.filter((emp) => emp.status === "COMPLETED").length;
    const pending = employees.filter((emp) => emp.status === "PENDING" || emp.status === "IN_PROGRESS").length;
    const failed = employees.filter((emp) => emp.status === "FAILED" || emp.status === "REJECTED").length;
    return { total, completed, pending, failed };
  }, [employees]);

  const [viewMode, setViewMode] = useState("list");
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });

  const paginatedData = useMemo(() => {
    const startIndex = paginationModel.page * paginationModel.pageSize;
    return filteredData.slice(startIndex, startIndex + paginationModel.pageSize);
  }, [filteredData, paginationModel]);

  const handlePageChange = (event, newPage) => {
    setPaginationModel((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (event) => {
    setPaginationModel({
      page: 0,
      pageSize: parseInt(event.target.value, 10),
    });
  };

  return (
    <>
      <Container maxWidth="xl" sx={{ m: 0, p: 3, maxWidth: "100% !important" }}>
        <Grid container spacing={3}>
          {/* Page Header & Actions */}
          <Grid item size={{ xs: 12 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", md: "center" },
                gap: 2.5,
                mb: 1,
              }}
            >
              <Box>
                <Breadcrumbs
                  separator={<NavigateNextIcon sx={{ fontSize: "1rem" }} />}
                  sx={{
                    marginBottom: 1,
                    "& .MuiBreadcrumbs-li": {
                      fontSize: "0.875rem",
                    },
                  }}
                >
                  {currentBreadcrumbs.map((crumb, index) => (
                    <Link
                      key={crumb.path}
                      underline={
                        index === currentBreadcrumbs.length - 1 ? "none" : "hover"
                      }
                      color="inherit"
                      sx={{
                        cursor:
                          index === currentBreadcrumbs.length - 1
                            ? "default"
                            : "pointer",
                        color:
                          index === currentBreadcrumbs.length - 1
                            ? "#7522D1"
                            : "#4F4937",
                        fontWeight:
                          index === currentBreadcrumbs.length - 1 ? 600 : 400,
                        "&:hover": {
                          color:
                            index === currentBreadcrumbs.length - 1
                              ? "#7522D1"
                              : "#7522D1",
                        },
                      }}
                      onClick={() =>
                        index < currentBreadcrumbs.length - 1 &&
                        navigate(crumb.path)
                      }
                    >
                      {crumb.label}
                    </Link>
                  ))}
                </Breadcrumbs>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    color: "#030007",
                    marginBottom: 0.5,
                    letterSpacing: "-0.02em",
                  }}
                >
                  Employee Directory
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#4F4937",
                    fontSize: "0.95rem",
                  }}
                >
                  Manage employee records, track roles, and keep organizational information up to date.
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  flexWrap: "wrap",
                  alignSelf: { xs: "stretch", md: "center" },
                  justifyContent: { xs: "flex-start", md: "flex-end" },
                }}
              >
                <PermissionGuard module="employees" action="create">
                  <IconHoverButton
                    key="add-employee"
                    icon={<PersonAdd fontSize="24px" />}
                    color="primary"
                    variant="contained"
                    onClick={() => navigate("/my-team/add-employee")}
                    sx={{
                      backgroundColor: "#7522D1",
                      borderRadius: "10px",
                      boxShadow: "0 4px 12px rgba(117, 34, 209, 0.15)",
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        backgroundColor: "#5e1ba8",
                        boxShadow: "0 6px 16px rgba(117, 34, 209, 0.25)",
                        transform: "translateY(-2px)",
                      },
                      "&:active": {
                        transform: "translateY(0)",
                      },
                    }}
                  >
                    Add Employee
                  </IconHoverButton>
                </PermissionGuard>

                <PermissionGuard module="onboarding" action="create">
                  <IconHoverButton
                    key="invite-employee"
                    icon={<AttachEmail />}
                    color="primary"
                    variant="contained"
                    onClick={() => navigate("/pre-onboarding/initiate")}
                    sx={{
                      backgroundColor: "#7522D1",
                      borderRadius: "10px",
                      boxShadow: "0 4px 12px rgba(117, 34, 209, 0.15)",
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        backgroundColor: "#5e1ba8",
                        boxShadow: "0 6px 16px rgba(117, 34, 209, 0.25)",
                        transform: "translateY(-2px)",
                      },
                      "&:active": {
                        transform: "translateY(0)",
                      },
                    }}
                  >
                    Invite Employee
                  </IconHoverButton>
                </PermissionGuard>

                <IconHoverButton
                  key="download-excel"
                  icon={<Download fontSize="24px" />}
                  color="secondary"
                  variant="outlined"
                  onClick={handleDownloadExcel}
                  sx={{
                    borderColor: "#7522D1",
                    color: "#7522D1",
                    borderRadius: "10px",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      borderColor: "#5e1ba8",
                      backgroundColor: "rgba(117, 34, 209, 0.04)",
                      transform: "translateY(-2px)",
                    },
                    "&:active": {
                      transform: "translateY(0)",
                    },
                  }}
                >
                  Download Excel
                </IconHoverButton>
              </Box>
            </Box>
          </Grid>

          {/* Stats Dashboard Cards */}
          <Grid item size={{ xs: 12 }}>
            <Grid container spacing={3}>
              <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title="Total Employees"
                  count={stats.total}
                  icon={<People sx={{ fontSize: 24 }} />}
                  color="#7522D1"
                  gradient="radial-gradient(circle, #7522D1 0%, rgba(117, 34, 209, 0) 70%)"
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title="Active Onboarding"
                  count={stats.completed}
                  icon={<CheckCircle sx={{ fontSize: 24 }} />}
                  color="#10B981"
                  gradient="radial-gradient(circle, #10B981 0%, rgba(16, 185, 129, 0) 70%)"
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title="Pending Onboarding"
                  count={stats.pending}
                  icon={<PendingActions sx={{ fontSize: 24 }} />}
                  color="#F59E0B"
                  gradient="radial-gradient(circle, #F59E0B 0%, rgba(245, 158, 11, 0) 70%)"
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title="Failed Onboarding"
                  count={stats.failed}
                  icon={<ErrorOutline sx={{ fontSize: 24 }} />}
                  color="#EF4444"
                  gradient="radial-gradient(circle, #EF4444 0%, rgba(239, 68, 68, 0) 70%)"
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Filters Control Panel */}
          <Grid item size={{ xs: 12 }}>
            <Box
              sx={{
                p: 2.5,
                borderRadius: "16px",
                backgroundColor: "rgba(255, 255, 255, 0.75)",
                backdropFilter: "blur(12px)",
                border: "1px solid rgba(226, 232, 240, 0.8)",
                boxShadow: "0 8px 32px rgba(148, 163, 184, 0.04)",
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item size={{ xs: 12, md: 3 }}>
                  <TextField
                    placeholder="Search by ID, Name, Email..."
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: "text.secondary" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": filterInputStyle,
                    }}
                  />
                </Grid>

                {(user?.role?.hierarchyLevel === "Level 1" ||
                  user?.role?.code === "SUPER_ADMIN") && (
                  <>
                    <Grid item size={{ xs: 12, sm: 6, md: 2 }}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Entity</InputLabel>
                        <Select
                          value={entityFilter}
                          label="Entity"
                          onChange={(e) => setEntityFilter(e.target.value)}
                          sx={filterInputStyle}
                        >
                          <MenuItem value="ALL">All Entities</MenuItem>
                          {entities.map((ent) => (
                            <MenuItem key={ent.id || ent._id} value={ent.id || ent._id}>
                              {ent.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item size={{ xs: 12, sm: 6, md: 2 }}>
                      <FormControl size="small" fullWidth disabled={entityFilter === "ALL"}>
                        <InputLabel>Work Location</InputLabel>
                        <Select
                          value={locationFilter}
                          label="Work Location"
                          onChange={(e) => setLocationFilter(e.target.value)}
                          sx={filterInputStyle}
                        >
                          <MenuItem value="ALL">All Locations</MenuItem>
                          {locations.map((loc) => (
                            <MenuItem key={loc.id || loc._id} value={loc.id || loc._id}>
                              {loc.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}

                <Grid item size={{ xs: 12, sm: 6, md: 2 }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Employee Type</InputLabel>
                    <Select
                      value={employeeTypeFilter}
                      label="Employee Type"
                      onChange={(e) => setEmployeeTypeFilter(e.target.value)}
                      sx={filterInputStyle}
                    >
                      <MenuItem value="ALL">All Types</MenuItem>
                      <MenuItem value="FULL_TIME">Full Time</MenuItem>
                      <MenuItem value="PART_TIME">Part Time</MenuItem>
                      <MenuItem value="CONTRACT">Contract</MenuItem>
                      <MenuItem value="INTERN">Intern</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item size={{ xs: 12, sm: 6, md: 2 }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      label="Status"
                      onChange={(e) => setStatusFilter(e.target.value)}
                      sx={filterInputStyle}
                    >
                      <MenuItem value="ALL">All Status</MenuItem>
                      <MenuItem value="PENDING">Pending</MenuItem>
                      <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                      <MenuItem value="COMPLETED">Completed</MenuItem>
                      <MenuItem value="REJECTED">Rejected</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item size={{ xs: 12, md: 1 }} sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
                  <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={(e, next) => next && setViewMode(next)}
                    size="small"
                    aria-label="view mode toggle"
                    sx={{
                      backgroundColor: "#fff",
                      borderRadius: "10px",
                      p: 0.5,
                      border: "1px solid rgba(226, 232, 240, 0.8)",
                      "& .MuiToggleButton-root": {
                        border: "none",
                        borderRadius: "8px",
                        px: 1.5,
                        py: 0.5,
                        color: "text.secondary",
                        "&.Mui-selected": {
                          backgroundColor: "rgba(117, 34, 209, 0.08)",
                          color: "#7522D1",
                          "&:hover": {
                            backgroundColor: "rgba(117, 34, 209, 0.12)",
                          },
                        },
                      },
                    }}
                  >
                    <ToggleButton value="list" aria-label="list view">
                      <ViewList sx={{ fontSize: 20 }} />
                    </ToggleButton>
                    <ToggleButton value="grid" aria-label="grid view">
                      <GridView sx={{ fontSize: 20 }} />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* Directory Content Section */}
          <Grid item size={{ xs: 12 }} sx={{ mb: 2 }}>
            {viewMode === "list" ? (
              <Box
                sx={{
                  width: "100%",
                  overflow: "hidden",
                  borderRadius: "16px",
                  border: "1px solid rgba(226, 232, 240, 0.8)",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.02)",
                  backgroundColor: "#fff",
                }}
              >
                <DataGrid
                  rows={filteredData}
                  columns={columns}
                  disableRowSelectionOnClick
                  slots={{
                    noRowsOverlay: CustomNoResultsOverlay,
                  }}
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                  rowHeight={60}
                  sx={{
                    height: 540,
                    width: "100%",
                    border: "none",
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: "rgba(117, 34, 209, 0.04)",
                      borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
                    },
                    "& .MuiDataGrid-columnHeader": {
                      color: "text.primary",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.75rem",
                      letterSpacing: "0.05em",
                    },
                    "& .MuiDataGrid-row": {
                      transition: "background-color 0.2s ease",
                      "&:hover": {
                        backgroundColor: "rgba(117, 34, 209, 0.015)",
                      },
                    },
                    "& .MuiDataGrid-cell": {
                      borderBottom: "1px solid rgba(241, 245, 249, 0.8)",
                    },
                    "& .MuiDataGrid-footerContainer": {
                      borderTop: "1px solid rgba(226, 232, 240, 0.8)",
                      backgroundColor: "#fff",
                    },
                  }}
                  loading={loading}
                  pageSizeOptions={[5, 10, 25, 50]}
                />
              </Box>
            ) : (
              <Box>
                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
                    <CircularProgress size={40} sx={{ color: "#7522D1" }} />
                  </Box>
                ) : filteredData.length === 0 ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                    <CustomNoResultsOverlay />
                  </Box>
                ) : (
                  <>
                    <Grid container spacing={3}>
                      {paginatedData.map((employee) => (
                        <Grid item key={employee.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                          <EmployeeCard
                            employee={employee}
                            onActionOpen={handleMenuOpen}
                            navigate={navigate}
                          />
                        </Grid>
                      ))}
                    </Grid>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, backgroundColor: "#fff", borderRadius: "12px", border: "1px solid rgba(226, 232, 240, 0.8)", p: 0.5 }}>
                      <TablePagination
                        component="div"
                        count={filteredData.length}
                        page={paginationModel.page}
                        onPageChange={handlePageChange}
                        rowsPerPage={paginationModel.pageSize}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                      />
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Grid>
        </Grid>

        {/* Dialogs */}

        <Dialog
          open={openModal}
          onClose={handleCloseModal}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            Initiate Onboarding
            <IconButton
              onClick={handleCloseModal}
              sx={{
                color: "text.secondary",

                "&:hover": {
                  color: "text.primary",

                  backgroundColor: "action.hover",
                },
              }}
            >
              <Close />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ pb: 3 }}>
            <Box sx={{ mt: 1 }}>
              <form onSubmit={handleOnboardingSubmit}>
                <Grid container spacing={2}>
                  {/* Name Section */}

                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 0 }}>
                      <Person
                        sx={{ mr: 1, color: theme.palette.primary.main }}
                      />

                      <Typography
                        variant="h6"
                        sx={{
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                        }}
                      >
                        Employee Details
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField
                      fullWidth
                      label="First Name *"
                      value={onboardingFormData.firstName}
                      onChange={handleOnboardingInputChange("firstName")}
                      error={!!onboardingFormErrors.firstName}
                      helperText={onboardingFormErrors.firstName}
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,

                          "&:hover fieldset": {
                            borderColor: theme.palette.primary.main,
                          },

                          "&.Mui-focused fieldset": {
                            borderColor: theme.palette.primary.main,
                          },
                        },
                      }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField
                      fullWidth
                      label="Middle Name"
                      value={onboardingFormData.middleName}
                      onChange={handleOnboardingInputChange("middleName")}
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,

                          "&:hover fieldset": {
                            borderColor: theme.palette.primary.main,
                          },

                          "&.Mui-focused fieldset": {
                            borderColor: theme.palette.primary.main,
                          },
                        },
                      }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField
                      fullWidth
                      label="Last Name *"
                      value={onboardingFormData.lastName}
                      onChange={handleOnboardingInputChange("lastName")}
                      error={!!onboardingFormErrors.lastName}
                      helperText={onboardingFormErrors.lastName}
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,

                          "&:hover fieldset": {
                            borderColor: theme.palette.primary.main,
                          },

                          "&.Mui-focused fieldset": {
                            borderColor: theme.palette.primary.main,
                          },
                        },
                      }}
                    />
                  </Grid>

                  {/* Contact Information */}

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      label="Mobile Number *"
                      inputProps={{ maxLength: 10 }}
                      value={onboardingFormData.mobileNumber}
                      onChange={handleOnboardingInputChange("mobileNumber")}
                      error={!!onboardingFormErrors.mobileNumber}
                      helperText={onboardingFormErrors.mobileNumber}
                      placeholder="Enter 10-digit mobile number"
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,

                          "&:hover fieldset": {
                            borderColor: theme.palette.primary.main,
                          },

                          "&.Mui-focused fieldset": {
                            borderColor: theme.palette.primary.main,
                          },
                        },
                      }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      fullWidth
                      label="Email Address *"
                      type="email"
                      value={onboardingFormData.email}
                      onChange={handleOnboardingInputChange("email")}
                      error={!!onboardingFormErrors.email}
                      helperText={onboardingFormErrors.email}
                      placeholder="employee@company.com"
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,

                          "&:hover fieldset": {
                            borderColor: theme.palette.primary.main,
                          },

                          "&.Mui-focused fieldset": {
                            borderColor: theme.palette.primary.main,
                          },
                        },
                      }}
                    />
                  </Grid>

                  {/* Job Details */}

                  <Grid size={{ xs: 12 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 0,
                        mt: 2,
                      }}
                    >
                      <Business
                        sx={{ mr: 1, color: theme.palette.primary.main }}
                      />

                      <Typography
                        variant="h6"
                        sx={{
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                        }}
                      >
                        Job Details
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <FormControl
                      fullWidth
                      error={!!onboardingFormErrors.department}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,

                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme.palette.primary.main,
                          },

                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme.palette.primary.main,
                          },
                        },
                      }}
                    >
                      <InputLabel>Department *</InputLabel>

                      <Select
                        value={onboardingFormData.department}
                        onChange={handleOnboardingInputChange("department")}
                        label="Department *"
                      >
                        {departments.map((dept) => (
                          <MenuItem key={dept} value={dept}>
                            {dept}
                          </MenuItem>
                        ))}
                      </Select>

                      {onboardingFormErrors.department && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 0.5, ml: 2 }}
                        >
                          {onboardingFormErrors.department}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <FormControl
                      fullWidth
                      error={!!onboardingFormErrors.role}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,

                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme.palette.primary.main,
                          },

                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme.palette.primary.main,
                          },
                        },
                      }}
                    >
                      <InputLabel>Role *</InputLabel>

                      <Select
                        value={onboardingFormData.role}
                        onChange={handleOnboardingInputChange("role")}
                        label="Role *"
                      >
                        {roles.map((role) => (
                          <MenuItem key={role.id} value={role.id}>
                            {role.name}
                          </MenuItem>
                        ))}
                      </Select>

                      {onboardingFormErrors.role && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 0.5, ml: 2 }}
                        >
                          {onboardingFormErrors.role}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <FormControl
                      fullWidth
                      error={!!onboardingFormErrors.entity}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,

                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme.palette.primary.main,
                          },

                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme.palette.primary.main,
                          },
                        },
                      }}
                    >
                      <InputLabel>Entity *</InputLabel>

                      <Select
                        value={onboardingFormData.entity}
                        onChange={handleOnboardingInputChange("entity")}
                        label="Entity *"
                      >
                        {entities.map((entity) => (
                          <MenuItem key={entity.id} value={entity.id}>
                            {entity.name}
                          </MenuItem>
                        ))}
                      </Select>

                      {onboardingFormErrors.entity && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 0.5, ml: 2 }}
                        >
                          {onboardingFormErrors.entity}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  {/* Action Buttons */}

                  <Grid size={{ xs: 12 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "end",
                        gap: 2,
                        mt: 4,
                      }}
                    >
                      <Button
                        type="button"
                        variant="outlined"
                        onClick={handleOnboardingReset}
                        sx={{
                          borderRadius: 2,

                          borderColor: theme.palette.primary.main,

                          color: theme.palette.primary.main,

                          px: 4,

                          py: 1.5,

                          fontWeight: 600,

                          "&:hover": {
                            borderColor: theme.palette.primary.dark,

                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        Reset Form
                      </Button>

                      <Button
                        type="submit"
                        variant="contained"
                        sx={{
                          borderRadius: 2,

                          backgroundColor: theme.palette.primary.main,

                          color: theme.palette.common.white,

                          px: 4,

                          py: 1.5,

                          fontWeight: 600,

                          "&:hover": {
                            backgroundColor: theme.palette.primary.dark,
                          },
                        }}
                      >
                        Save Employee
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            </Box>
          </DialogContent>
        </Dialog>

        <Dialog
          open={openBulkUploadModal}
          onClose={handleCloseBulkUploadModal}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Bulk Upload</DialogTitle>

          <DialogContent>
            <Typography variant="body2" sx={{ color: "#4F4937", mb: 2 }}>
              Upload an Excel file (.xlsx or .xls)
            </Typography>

            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadFileIcon />}
            >
              Choose Excel File
              <input
                type="file"
                hidden
                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                onChange={handleBulkUploadFileChange}
              />
            </Button>

            {bulkUploadFile ? (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Selected: {bulkUploadFile.name}
              </Typography>
            ) : null}
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseBulkUploadModal}>Cancel</Button>

            <Button
              variant="contained"
              onClick={handleBulkUploadSubmit}
              disabled={!bulkUploadFile}
              sx={{
                backgroundColor: "#7522D1",

                "&:hover": { backgroundColor: "#5e1ba8" },
              }}
            >
              Upload
            </Button>
          </DialogActions>
        </Dialog>

        {/* Action Menu */}

        <Menu
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleMenuClose}
          PaperProps={{
            elevation: 2,

            sx: {
              borderRadius: 2,

              minWidth: 200,

              mt: 1,

              "& .MuiMenuItem-root": {
                py: 1.5,

                px: 2,
              },
            },
          }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <PermissionGuard module="onboarding" action="create">
            <MenuItem onClick={handleResendLink} sx={{ color: "#7522D1" }}>
              <ListItemIcon>
                <MailIcon fontSize="small" sx={{ color: "#7522D1" }} />
              </ListItemIcon>

              <ListItemText
                primary="Resend Onboarding Link"
                primaryTypographyProps={{ variant: "body2", fontWeight: 500 }}
              />
            </MenuItem>
          </PermissionGuard>

          <MenuItem onClick={handleViewDetails}>
            <ListItemIcon>
              <Person sx={{ fontSize: 20 }} />
            </ListItemIcon>

            <ListItemText
              primary="View Details"
              primaryTypographyProps={{ variant: "body2" }}
            />
          </MenuItem>

          <PermissionGuard module="employees" action="update">
            <MenuItem onClick={handleEdit}>
              <ListItemIcon>
                <EditIcon sx={{ fontSize: 20 }} />
              </ListItemIcon>

              <ListItemText
                primary="Edit"
                primaryTypographyProps={{ variant: "body2" }}
              />
            </MenuItem>
          </PermissionGuard>
        </Menu>

        {/* Feedback Notifications */}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: "100%", borderRadius: 2 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Add ErrorModal component */}

        <ErrorModal />

        {/* Alert Dialog */}

        <AlertDialog
          open={alertDialog.open}
          onClose={closeAlert}
          title={alertDialog.title}
          message={alertDialog.message}
          severity={alertDialog.severity}
          confirmText={alertDialog.confirmText}
          cancelText={alertDialog.cancelText}
          showCancel={alertDialog.showCancel}
          onConfirm={alertDialog.onConfirm}
        />
      </Container>
    </>
  );
};

export default EmployeeDirectory;
